import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

/*
	Norce keeps some product groupings as a text parametric holding a
	comma-separated PartNo list — the multi pack's item pool ("Mixflak") and
	the PDP's sibling variants ("Flera-varianter") both work that way. These
	helpers read and resolve such a list; the features themselves live in
	`multiPack.ts` and `productVariants.ts`.
*/

interface CodeValueLike {
	Code?: string | null;
	Value?: string | null;
}

/** A full Norce product or variant — the only shape carrying text parametrics. */
export interface ParametricPartNoSource {
	Parametrics?: CodeValueLike[] | null;
	VariantParametrics?: CodeValueLike[] | null;
}

/** First non-empty value for `code`, variant parametrics included. */
export const readParametricByCode = (
	sources: (ParametricPartNoSource | undefined | null)[],
	code: string
): string | undefined => {
	if (!code) return undefined;
	for (const source of sources) {
		for (const list of [source?.Parametrics, source?.VariantParametrics]) {
			const match = list?.find((parametric) => parametric?.Code === code);
			if (match?.Value != null && match.Value !== "") return match.Value;
		}
	}
	return undefined;
};

/** "204960, 204959; 204958" → ["204960", "204959", "204958"], order kept. */
export const parsePartNoList = (value: string | undefined): string[] => [
	...new Set(
		String(value ?? "")
			.split(/[,;\s]+/)
			.map((partNo) => partNo.trim())
			.filter(Boolean)
	)
];

/**
 * Pool in catalogue order. Norce returns `ListProductsByPartNos` results in
 * its own order, but the parametric's order is what the merchandiser chose.
 */
export const sortPoolByPartNos = <T extends Pick<ProductItem, "PartNo">>(
	pool: T[],
	partNos: string[]
): T[] =>
	partNos
		.map((partNo) => pool.find((item) => item.PartNo === partNo))
		.filter((item): item is T => !!item);
